import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import { MobileChaptersView } from '../components/mobile-chapters/MobileChaptersView';

// ── TypeScript Interfaces ─────────────────────────────────────

interface Chapter {
  id: string;
  name: string;
  city: string;
  state: string;
  country: string;
  contactEmail: string | null;
  contactPhone: string | null;
  presidentName: string;
  secretaryName: string;
  inceptionYear: number | null;
  reunionHostYears: string | null;
  chapterFact: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ChapterFormData {
  name: string;
  city: string;
  state: string;
  country: string;
  contactEmail: string;
  contactPhone: string;
  presidentName: string;
  secretaryName: string;
  inceptionYear: string;
  reunionHostYears: string;
  chapterFact: string;
}

// ── Countries List ────────────────────────────────────────────

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Jamaica', 'Trinidad and Tobago',
  'Barbados', 'Bahamas', 'Guyana', 'Antigua and Barbuda', 'Dominica',
  'Grenada', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Belize', 'Bermuda', 'Cayman Islands', 'Turks and Caicos Islands',
  'Ghana', 'Nigeria', 'Kenya', 'South Africa', 'Tanzania', 'Uganda',
  'Ethiopia', 'Cameroon', 'Senegal', 'Ivory Coast', 'Zimbabwe',
  'Australia', 'New Zealand', 'India', 'Germany', 'France', 'Netherlands',
  'Belgium', 'Switzerland', 'Italy', 'Spain', 'Brazil', 'Panama', 'Costa Rica',
].sort();

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' as const, gap: '0.75rem' },
  filterBar: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' as const, marginBottom: '1rem' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  select: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnSecondary: { padding: '0.5rem 1rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  toggleActive: { padding: '0.4rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  toggleInactive: { padding: '0.4rem 0.75rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: '0.5rem' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' },
  card: { padding: '1.25rem', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa', transition: 'background-color 0.15s' },
  cardName: { fontSize: '1.1rem', fontWeight: 600, color: '#0f2d1f', marginBottom: '0.5rem' },
  reunionHostBadge: { display: 'inline-block', backgroundColor: '#c9a84c', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '3px', letterSpacing: '0.05em', marginBottom: '0.5rem' },
  cardDetail: { fontSize: '0.85rem', color: '#555', marginBottom: '0.3rem' },
  cardActions: { marginTop: '0.75rem', display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1, transition: 'background-color 0.15s' },
  iconBtnDanger: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1, transition: 'background-color 0.15s', opacity: 0.7 },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem 0.5rem', borderBottom: '2px solid #e0e0e0', fontWeight: 600, color: '#0f2d1f', fontSize: '0.85rem' },
  td: { padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0', fontSize: '0.85rem', color: '#333' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
  // Modal styles
  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '8px', padding: '2rem', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' as const, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' },
  modalTitle: { color: '#0f2d1f', fontSize: '1.3rem', marginBottom: '1.5rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.5rem' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  fieldError: { color: '#d32f2f', fontSize: '0.75rem', marginTop: '0.25rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' },
  // Detail view styles
  detailCard: { padding: '1.25rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', minHeight: '80px' },
  detailLabel: { fontSize: '0.7rem', fontWeight: 700, color: '#1a6b3c', letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: '6px' },
  detailValue: { fontSize: '1.4rem', color: '#374151', fontWeight: 600, textAlign: 'center' as const },
  factSection: { marginTop: '1.5rem', padding: '1.25rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' },
  factText: { fontSize: '0.95rem', color: '#374151', lineHeight: 1.6, margin: '0' },
};

// ── Helper: Email validation ──────────────────────────────────

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ── ChapterFormModal Component ────────────────────────────────

function ChapterFormModal({
  chapter,
  onClose,
  onSaved,
}: {
  chapter: Chapter | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ChapterFormData>({
    name: chapter?.name || '',
    city: chapter?.city || '',
    state: chapter?.state || '',
    country: chapter?.country || '',
    contactEmail: chapter?.contactEmail || '',
    contactPhone: chapter?.contactPhone || '',
    presidentName: chapter?.presidentName || '',
    secretaryName: chapter?.secretaryName || '',
    inceptionYear: chapter?.inceptionYear?.toString() || '',
    reunionHostYears: chapter?.reunionHostYears || '',
    chapterFact: chapter?.chapterFact || '',
  });
  const [countrySearch, setCountrySearch] = useState(chapter?.country || '');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const filteredCountries = COUNTRIES.filter(c =>
    c.toLowerCase().includes(countrySearch.toLowerCase())
  );

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.city.trim()) errors.city = 'City is required';
    if (!form.state.trim()) errors.state = 'State is required';
    if (!form.country.trim()) errors.country = 'Country is required';
    if (form.contactEmail.trim() && !isValidEmail(form.contactEmail.trim())) {
      errors.contactEmail = 'Invalid email format';
    }
    if (!form.presidentName.trim()) errors.presidentName = 'President name is required';
    if (!form.secretaryName.trim()) errors.secretaryName = 'Secretary name is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setApiError('');
    try {
      const body = {
        name: form.name.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        country: form.country.trim(),
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        presidentName: form.presidentName.trim(),
        secretaryName: form.secretaryName.trim(),
        inceptionYear: form.inceptionYear ? parseInt(form.inceptionYear, 10) : undefined,
        reunionHostYears: form.reunionHostYears.trim() || undefined,
        chapterFact: form.chapterFact.trim() || undefined,
      };

      if (chapter) {
        await apiClient(`/chapters/${chapter.id}`, { method: 'PATCH', body });
      } else {
        await apiClient('/chapters', { method: 'POST', body });
      }
      onSaved();
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr.statusCode === 409) {
        setFieldErrors((prev) => ({ ...prev, name: 'A chapter with this name already exists' }));
      } else {
        setApiError(apiErr.message || 'Failed to save chapter.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleChange(field: keyof ChapterFormData, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={styles.modalTitle}>{chapter ? 'Edit Chapter' : 'Add Chapter'}</h2>
        {apiError && <div style={styles.error}>{apiError}</div>}
        <form onSubmit={handleSubmit}>
          <div style={styles.formGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Name *</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                aria-label="Chapter name"
              />
              {fieldErrors.name && <div style={styles.fieldError}>{fieldErrors.name}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>City *</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
                aria-label="City"
              />
              {fieldErrors.city && <div style={styles.fieldError}>{fieldErrors.city}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>State *</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
                aria-label="State"
              />
              {fieldErrors.state && <div style={styles.fieldError}>{fieldErrors.state}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Country *</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...styles.input, width: '100%' }}
                  value={countrySearch}
                  onChange={(e) => {
                    setCountrySearch(e.target.value);
                    handleChange('country', e.target.value);
                    setShowCountryDropdown(true);
                  }}
                  onFocus={() => setShowCountryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCountryDropdown(false), 150)}
                  placeholder="Type to search countries..."
                  aria-label="Country"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '150px', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '0 0 4px 4px', zIndex: 10, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
                    {filteredCountries.slice(0, 10).map(c => (
                      <div
                        key={c}
                        style={{ padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', backgroundColor: c === form.country ? '#e8f5e9' : '#fff' }}
                        onMouseDown={() => {
                          handleChange('country', c);
                          setCountrySearch(c);
                          setShowCountryDropdown(false);
                        }}
                      >
                        {c}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {fieldErrors.country && <div style={styles.fieldError}>{fieldErrors.country}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Contact Email</label>
              <input
                type="email"
                style={{ ...styles.input, width: '100%' }}
                value={form.contactEmail}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
                aria-label="Contact email"
              />
              {fieldErrors.contactEmail && <div style={styles.fieldError}>{fieldErrors.contactEmail}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Contact Phone</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={form.contactPhone}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
                aria-label="Contact phone"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>President Name *</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={form.presidentName}
                onChange={(e) => handleChange('presidentName', e.target.value)}
                aria-label="President name"
              />
              {fieldErrors.presidentName && <div style={styles.fieldError}>{fieldErrors.presidentName}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Secretary Name *</label>
              <input
                style={{ ...styles.input, width: '100%' }}
                value={form.secretaryName}
                onChange={(e) => handleChange('secretaryName', e.target.value)}
                aria-label="Secretary name"
              />
              {fieldErrors.secretaryName && <div style={styles.fieldError}>{fieldErrors.secretaryName}</div>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Inception Year</label>
              <select
                style={{ ...styles.select, width: '100%' }}
                value={form.inceptionYear}
                onChange={(e) => handleChange('inceptionYear', e.target.value)}
                aria-label="Inception year"
              >
                <option value="">Select year...</option>
                {Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year.toString()}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Reunion Host Year(s)</label>
            <input
              style={{ ...styles.input, width: '100%' }}
              value={form.reunionHostYears}
              onChange={(e) => handleChange('reunionHostYears', e.target.value)}
              maxLength={200}
              placeholder="e.g. 2018, 2022"
              aria-label="Reunion host years"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Chapter Fact</label>
            <textarea
              style={{ ...styles.input, width: '100%', minHeight: '80px', resize: 'vertical' }}
              value={form.chapterFact}
              onChange={(e) => handleChange('chapterFact', e.target.value)}
              placeholder="An interesting fact about this chapter..."
              aria-label="Chapter fact"
            />
          </div>
          <div style={styles.modalActions}>
            <button type="button" style={styles.btnSecondary} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.btn} disabled={submitting}>
              {submitting ? 'Saving...' : chapter ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────

export function ChaptersPage() {
  const { user } = useAuth();
  const canManage = isAtLeast(user?.role || '', 'Organizer');

  // Data state
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  // View state
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  // Detail view state
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const [chapterGuests, setChapterGuests] = useState<{ id: string; fullName: string; ticketNumber: string | null; tableName: string | null }[]>([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);

  // Debounce ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Guest Count Fetch ───────────────────────────────────────

  useEffect(() => {
    if (selectedChapter) {
      setGuestCount(null);
      setChapterGuests([]);
      apiClient<{ count: number }>(`/chapters/${selectedChapter.id}/guest-count`)
        .then(({ count }) => setGuestCount(count))
        .catch(() => setGuestCount(0));
      apiClient<{ guests: { id: string; fullName: string; ticketNumber: string | null; tableName: string | null }[] }>(`/chapters/${selectedChapter.id}/guests`)
        .then(({ guests }) => setChapterGuests(guests))
        .catch(() => setChapterGuests([]));
    }
  }, [selectedChapter]);

  // ── Data Fetching ─────────────────────────────────────────

  const fetchChapters = useCallback(async (search?: string, country?: string) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (country) params.set('country', country);
      const query = params.toString() ? `?${params.toString()}` : '';
      const data = await apiClient<Chapter[]>(`/chapters${query}`);
      setChapters(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load chapters.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChapters();
  }, [fetchChapters]);

  // Debounced search
  function handleSearchChange(value: string) {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchChapters(value, countryFilter);
    }, 300);
  }

  function handleCountryChange(value: string) {
    setCountryFilter(value);
    fetchChapters(searchText, value);
  }

  // ── CRUD Handlers ─────────────────────────────────────────

  function handleAdd() {
    setEditingChapter(null);
    setShowModal(true);
  }

  function handleEdit(chapter: Chapter) {
    setEditingChapter(chapter);
    setShowModal(true);
  }

  async function handleDelete(chapter: Chapter) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${chapter.name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await apiClient(`/chapters/${chapter.id}`, { method: 'DELETE' });
      fetchChapters(searchText, countryFilter);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete chapter.');
    }
  }

  function handleModalSaved() {
    setShowModal(false);
    setEditingChapter(null);
    fetchChapters(searchText, countryFilter);
  }

  function handleModalClose() {
    setShowModal(false);
    setEditingChapter(null);
  }

  // ── Computed Values ───────────────────────────────────────

  const uniqueCountries = Array.from(new Set(chapters.map((c) => c.country))).sort();

  // ── Mobile detection ────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Render ────────────────────────────────────────────────

  if (isMobile) {
    return <MobileChaptersView />;
  }

  // ── Chapter Detail View ─────────────────────────────────────
  if (selectedChapter) {
    return (
      <div style={styles.page}>
        <button
          onClick={() => setSelectedChapter(null)}
          style={{ ...styles.btnSecondary, marginBottom: '1rem' }}
          aria-label="Back to chapters list"
        >
          ← Back to Chapters
        </button>

        <div style={styles.section}>
          <h2 style={{ ...styles.title, marginBottom: '1rem' }}>{selectedChapter.name}</h2>
          <div style={styles.cardDetail}>
            📍 {selectedChapter.city}, {selectedChapter.state}, {selectedChapter.country}
          </div>
          {selectedChapter.contactEmail && (
            <div style={styles.cardDetail}>✉️ {selectedChapter.contactEmail}</div>
          )}
          {selectedChapter.contactPhone && (
            <div style={styles.cardDetail}>📞 {selectedChapter.contactPhone}</div>
          )}
          <div style={styles.cardDetail}>👤 President: {selectedChapter.presidentName}</div>
          <div style={styles.cardDetail}>📋 Secretary: {selectedChapter.secretaryName}</div>
          {selectedChapter.inceptionYear && (
            <div style={styles.cardDetail}>📅 Est. {selectedChapter.inceptionYear}</div>
          )}

          {/* Detail tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            {/* Guest Count tile */}
            <div style={styles.detailCard}>
              <div style={styles.detailLabel}>GUESTS ATTENDING</div>
              <div style={styles.detailValue}>{guestCount ?? '...'}</div>
              {chapterGuests.length > 0 && (() => {
                const tableNames = [...new Set(chapterGuests.map(g => g.tableName).filter(Boolean))] as string[];
                return tableNames.length > 0 ? (
                  <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', fontWeight: 700, color: '#1a6b3c', lineHeight: 1.5 }}>
                    {tableNames.join(', ')}
                  </div>
                ) : null;
              })()}
            </div>
            {/* Reunion Host Years — only if non-empty */}
            {selectedChapter.reunionHostYears && (
              <div style={styles.detailCard}>
                <div style={styles.detailLabel}>REUNION HOST</div>
                <div style={styles.detailValue}>{selectedChapter.reunionHostYears}</div>
              </div>
            )}
          </div>

          {/* Chapter Fact — only if non-empty */}
          {selectedChapter.chapterFact && (
            <div style={styles.factSection}>
              <div style={styles.detailLabel}>CHAPTER FACT</div>
              <p style={styles.factText}>{selectedChapter.chapterFact}</p>
            </div>
          )}

          {canManage && (
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
              <button style={styles.btn} onClick={() => handleEdit(selectedChapter)}>
                ✏️ Edit
              </button>
              <button style={styles.btnDanger} onClick={() => handleDelete(selectedChapter)}>
                🗑️ Delete
              </button>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <ChapterFormModal
            chapter={editingChapter}
            onClose={handleModalClose}
            onSaved={handleModalSaved}
          />
        )}
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.title}>Chapters Directory</h1>
        {canManage && (
          <button style={styles.btn} onClick={handleAdd}>
            + Add Chapter
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Filter Bar */}
      <div style={styles.section}>
        <div style={styles.filterBar}>
          <input
            style={{ ...styles.input, minWidth: '200px', flex: 1 }}
            type="text"
            placeholder="Search by name, city, state, or country..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search chapters"
          />
          <select
            style={styles.select}
            value={countryFilter}
            onChange={(e) => handleCountryChange(e.target.value)}
            aria-label="Filter by country"
          >
            <option value="">All Countries</option>
            {uniqueCountries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          <div>
            <button
              style={viewMode === 'cards' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
            <button
              style={viewMode === 'table' ? styles.toggleActive : styles.toggleInactive}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={styles.loading}>Loading chapters...</div>
      ) : chapters.length === 0 ? (
        <div style={styles.emptyState}>No chapters found.</div>
      ) : viewMode === 'cards' ? (
        <div style={styles.cardGrid}>
          {chapters.map((chapter) => (
            <div key={chapter.id} style={{ ...styles.card, cursor: 'pointer' }} onClick={() => setSelectedChapter(chapter)} role="button" tabIndex={0} aria-label={`View ${chapter.name} details`}>
              <div style={styles.cardName}>{chapter.name}</div>
              {chapter.reunionHostYears && (
                <span style={styles.reunionHostBadge}>REUNION HOST</span>
              )}
              <div style={styles.cardDetail}>
                📍 {chapter.city}, {chapter.state}, {chapter.country}
              </div>
              {chapter.contactEmail && (
                <div style={styles.cardDetail}>✉️ {chapter.contactEmail}</div>
              )}
              {chapter.contactPhone && (
                <div style={styles.cardDetail}>📞 {chapter.contactPhone}</div>
              )}
              <div style={styles.cardDetail}>👤 President: {chapter.presidentName}</div>
              <div style={styles.cardDetail}>📋 Secretary: {chapter.secretaryName}</div>
              {chapter.inceptionYear && (
                <div style={styles.cardDetail}>📅 Est. {chapter.inceptionYear}</div>
              )}
              {canManage && (
                <div style={styles.cardActions}>
                  <button style={styles.iconBtn} onClick={() => handleEdit(chapter)} title="Edit chapter" aria-label={`Edit ${chapter.name}`}>
                    ✏️
                  </button>
                  <button style={styles.iconBtnDanger} onClick={() => handleDelete(chapter)} title="Delete chapter" aria-label={`Delete ${chapter.name}`}>
                    🗑️
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.section}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>City</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Country</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>President</th>
                  <th style={styles.th}>Secretary</th>
                  <th style={styles.th}>Est.</th>
                  {canManage && <th style={styles.th}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {chapters.map((chapter) => (
                  <tr key={chapter.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedChapter(chapter)}>
                    <td style={styles.td}>{chapter.name}</td>
                    <td style={styles.td}>{chapter.city}</td>
                    <td style={styles.td}>{chapter.state}</td>
                    <td style={styles.td}>{chapter.country}</td>
                    <td style={styles.td}>{chapter.contactEmail || '—'}</td>
                    <td style={styles.td}>{chapter.presidentName}</td>
                    <td style={styles.td}>{chapter.secretaryName}</td>
                    <td style={styles.td}>{chapter.inceptionYear || '—'}</td>
                    {canManage && (
                      <td style={styles.td}>
                        <button style={styles.iconBtn} onClick={() => handleEdit(chapter)} title="Edit chapter" aria-label={`Edit ${chapter.name}`}>
                          ✏️
                        </button>
                        <button style={styles.iconBtnDanger} onClick={() => handleDelete(chapter)} title="Delete chapter" aria-label={`Delete ${chapter.name}`}>
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <ChapterFormModal
          chapter={editingChapter}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}
